import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, CheckCircle, ChevronUp, ChevronDown, Search } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const TIPO_BADGE = { normal:'badge-green', distocico:'badge-red', cesariana:'badge-orange' };
const SEXO_COR   = { M:'#3B82F6', F:'#EC4899' };

function SortIcon({ col, sort }) {
  if (sort.col !== col) return <ChevronDown size={13} style={{ opacity:0.3 }}/>;
  return sort.dir === 'asc' ? <ChevronUp size={13}/> : <ChevronDown size={13}/>;
}

function sortRows(rows, sort) {
  return [...rows].sort((a, b) => {
    const va = a[sort.col] ?? '', vb = b[sort.col] ?? '';
    const dir = sort.dir === 'asc' ? 1 : -1;
    if (sort.col === 'data') return (new Date(va||0) - new Date(vb||0)) * dir;
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });
}

export default function Partos() {
  const [todos, setTodos] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  // Sort states
  const [sortPrev, setSortPrev] = useState({ col:'data_prevista', dir:'asc' });
  const [sortConf, setSortConf] = useState({ col:'data', dir:'desc' });

  // Modal: novo parto manual
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ mae_id:'', pai_id:'', data:todayLocalISO(), hora:'', tipo:'normal', num_crias_vivas:1, num_natimortos:0, num_mumificados:0, ocorrencias:'', observacoes:'', crias:[] });
  const CRIA_VAZIA = { brinco:'', sexo:'M', peso_nascimento:'' };

  // Modal: novo parto previsto manual (matriz com sinais de prenhez, sem cobertura registrada)
  const [showPrevisto, setShowPrevisto] = useState(false);
  const [formPrevisto, setFormPrevisto] = useState({ mae_id:'', pai_id:'', data_prevista:'', observacoes:'' });

  // Modal: confirmar parto previsto
  const [confirmando, setConfirmando] = useState(null);
  const [formConf, setFormConf] = useState({ data:todayLocalISO(), hora:'', tipo:'normal', num_crias_vivas:1, num_natimortos:0, num_mumificados:0, ocorrencias:'', observacoes:'', crias:[CRIA_VAZIA] });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data:d } = await api.get('/reproducao/partos'); setTodos(d); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/animais', { params:{ situacao:'ativo', limit:500 } }).then(r => setAnimais(r.data.data || r.data));
  }, []);

  const femeas = animais.filter(a => a.sexo === 'F');
  const machos = animais.filter(a => a.sexo === 'M');

  // Split previstos / confirmados — confirmado_em é a fonte de verdade (marcado no registro
  // direto e em /confirmar), independente dos contadores de crias.
  const isPrevisto = p => !p.confirmado_em;
  const buscaMatch = p => {
    if (!busca) return true;
    const t = busca.toLowerCase();
    return (p.mae_brinco||'').toLowerCase().includes(t) || (p.mae_nome||'').toLowerCase().includes(t);
  };

  const previstos   = todos.filter(p => isPrevisto(p) && buscaMatch(p));
  const confirmados = todos.filter(p => !isPrevisto(p) && buscaMatch(p));

  const prevSorted = sortRows(previstos, sortPrev);
  const confSorted = sortRows(confirmados, sortConf);

  // Novo parto manual
  const updateCrias = (n) => {
    const arr = Array(n).fill(null).map((_, i) => form.crias[i] || CRIA_VAZIA);
    setForm(p => ({ ...p, crias:arr, num_crias_vivas:n }));
  };

  const salvar = async () => {
    try {
      await api.post('/reproducao/partos', { ...form, crias: form.crias });
      toast.success('Parto registrado!'); setShowModal(false); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const salvarPrevisto = async () => {
    try {
      await api.post('/reproducao/partos/previsto', formPrevisto);
      toast.success('Parto previsto cadastrado!');
      setShowPrevisto(false);
      setFormPrevisto({ mae_id:'', pai_id:'', data_prevista:'', observacoes:'' });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este parto?')) return;
    try { await api.delete(`/reproducao/partos/${id}`); toast.success('Parto excluído'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  // Confirmar parto previsto
  const abrirConfirmar = (parto) => {
    setConfirmando(parto);
    setFormConf({ data:todayLocalISO(), hora:'', tipo:'normal', num_crias_vivas:1, num_natimortos:0, num_mumificados:0, ocorrencias:'', observacoes:'', crias:[CRIA_VAZIA] });
  };

  const updateCriasConf = (n) => {
    const arr = Array(Math.max(0,n)).fill(null).map((_, i) => formConf.crias[i] || CRIA_VAZIA);
    setFormConf(p => ({ ...p, num_crias_vivas:n, crias:arr }));
  };

  const setCriaConf = (i, field, val) => {
    const arr = [...formConf.crias];
    arr[i] = { ...arr[i], [field]: val };
    setFormConf(p => ({ ...p, crias:arr }));
  };

  const confirmarParto = async () => {
    try {
      await api.put(`/reproducao/partos/${confirmando.id}/confirmar`, formConf);
      toast.success('Parto confirmado! Crias cadastradas automaticamente.');
      setConfirmando(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao confirmar parto'); }
  };

  // Stats
  const totalConf  = confirmados.length;
  const totalCrias = confirmados.reduce((s, p) => s + (parseInt(p.num_crias_vivas)||0), 0);
  const gemelos    = confirmados.filter(p => p.num_crias_vivas >= 2).length;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Partos</h1><p>{previstos.length} previstos · {totalConf} confirmados</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setShowPrevisto(true)}><Plus size={16}/> Novo Parto Previsto</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16}/> Registrar Parto</button>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns:'repeat(4,1fr)' }}>
        {[['Previstos',previstos.length,'#D97706'],['Confirmados',totalConf,'var(--color-primary)'],['Crias Vivas',totalCrias,'var(--color-success)'],['Gemelares',gemelos,'var(--color-info)']].map(([l,v,c]) => (
          <div key={l} className="stat-card" style={{ borderLeft:`4px solid ${c}` }}>
            <div className="stat-label">{l}</div>
            <div className="stat-value" style={{ color:c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div className="search-box">
          <Search size={16}/>
          <input placeholder="Buscar por brinco ou nome da mãe..." value={busca} onChange={e => setBusca(e.target.value)}/>
        </div>
      </div>

      {/* SEÇÃO: PARTOS PREVISTOS */}
      <div style={{ marginBottom:24 }}>
        <h2 style={{ fontSize:15, fontWeight:600, color:'#D97706', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ background:'#FEF3C7', borderRadius:6, padding:'2px 10px' }}>Partos Previstos ({prevSorted.length})</span>
        </h2>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table>
            <thead><tr>
              {[['mae_brinco','Matriz'],['data_prevista','Parto Previsto'],['mae_categoria','Categoria'],['origem_previsao','Origem']].map(([col,label]) => (
                <th key={col} style={{ cursor:'pointer', userSelect:'none' }} onClick={() => setSortPrev(s => ({ col, dir: s.col===col && s.dir==='asc'?'desc':'asc' }))}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>{label} <SortIcon col={col} sort={sortPrev}/></span>
                </th>
              ))}
              <th>Ação</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{ textAlign:'center', padding:40 }}>Carregando...</td></tr>
              : prevSorted.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>Nenhum parto previsto</p></div></td></tr>
              : prevSorted.map(p => {
                const prevista = p.data_prevista || (p.data ? new Date(new Date(p.data).getTime() + 150*24*60*60*1000).toISOString().split('T')[0] : null);
                const diasFalta = prevista ? Math.ceil((new Date(prevista) - new Date()) / (1000*60*60*24)) : null;
                const atrasado  = diasFalta !== null && diasFalta < 0;
                const urgente   = diasFalta !== null && diasFalta <= 7 && diasFalta >= 0;
                return (
                  <tr key={p.id} style={{ background: atrasado ? '#FEF2F2' : urgente ? '#FFFBEB' : undefined }}>
                    <td><strong style={{ color:'var(--color-primary)' }}>{p.mae_brinco}</strong>{p.mae_nome && ` — ${p.mae_nome}`}</td>
                    <td>
                      {prevista ? formatDateBR(prevista) : '—'}
                      {atrasado && <span className="badge badge-red" style={{ marginLeft:6 }}>Atrasado</span>}
                      {urgente  && <span className="badge badge-yellow" style={{ marginLeft:6 }}>Em {diasFalta}d</span>}
                    </td>
                    <td><span className="badge badge-gray">{p.mae_categoria||'—'}</span></td>
                    <td><span className={`badge ${p.origem_previsao==='manual'?'badge-blue':'badge-gray'}`}>{p.origem_previsao==='manual'?'Manual':'Automático'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => abrirConfirmar(p)} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                          <CheckCircle size={14}/> Confirmar
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => excluir(p.id)} style={{ color:'#DC2626', padding:'4px 8px' }}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* SEÇÃO: PARTOS CONFIRMADOS */}
      <div>
        <h2 style={{ fontSize:15, fontWeight:600, color:'var(--color-primary)', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ background:'#EFF6FF', borderRadius:6, padding:'2px 10px' }}>Partos Confirmados ({confSorted.length})</span>
        </h2>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table>
            <thead><tr>
              {[['mae_brinco','Mãe'],['data','Data'],['tipo','Tipo'],['num_crias_vivas','Crias'],['num_natimortos','Natimortos'],['ocorrencias','Ocorrências']].map(([col,label]) => (
                <th key={col} style={{ cursor:'pointer', userSelect:'none' }} onClick={() => setSortConf(s => ({ col, dir: s.col===col && s.dir==='asc'?'desc':'asc' }))}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>{label} <SortIcon col={col} sort={sortConf}/></span>
                </th>
              ))}
              <th></th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} style={{ textAlign:'center', padding:40 }}>Carregando...</td></tr>
              : confSorted.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><p>Nenhum parto confirmado</p></div></td></tr>
              : confSorted.map(p => (
                <tr key={p.id}>
                  <td><strong style={{ color:'var(--color-primary)' }}>{p.mae_brinco}</strong>{p.mae_nome && ` — ${p.mae_nome}`}</td>
                  <td>{formatDateBR(p.data)}{p.hora && ` ${String(p.hora).substring(0,5)}`}</td>
                  <td><span className={`badge ${TIPO_BADGE[p.tipo]||'badge-gray'}`}>{p.tipo}</span></td>
                  <td>
                    <strong style={{ color:'var(--color-success)', fontSize:16 }}>{p.num_crias_vivas}</strong>
                    {p.num_crias_vivas >= 2 && <span className="badge badge-blue" style={{ marginLeft:6 }}>Gemelar</span>}
                  </td>
                  <td style={{ color: p.num_natimortos > 0 ? 'var(--color-danger)' : 'var(--text-secondary)' }}>{p.num_natimortos || 0}</td>
                  <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{p.ocorrencias || '—'}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => excluir(p.id)} style={{ color:'#DC2626', padding:'4px 8px' }}><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL: Registrar Parto Manual */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth:680 }}>
            <div className="modal-header"><h2>Registrar Parto</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Mãe *</label>
                  <AnimalSearchSelect value={form.mae_id} onChange={id => setForm(p => ({ ...p, mae_id:id }))} sexo="F" required />
                </div>
                <div className="form-group"><label>Pai (opcional)</label>
                  <AnimalSearchSelect value={form.pai_id || ''} onChange={id => setForm(p => ({ ...p, pai_id:id }))} sexo="M" emptyLabel="Não informado" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data *</label><input type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data:e.target.value }))}/></div>
                <div className="form-group"><label>Hora</label><input type="time" value={form.hora} onChange={e => setForm(p => ({ ...p, hora:e.target.value }))}/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Tipo do Parto</label>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo:e.target.value }))}>
                    <option value="normal">Normal</option><option value="distocico">Distócico</option><option value="cesariana">Cesariana</option>
                  </select>
                </div>
                <div className="form-group"><label>Crias Vivas</label>
                  <input type="number" min="0" value={form.num_crias_vivas} onChange={e => updateCrias(parseInt(e.target.value)||0)}/>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Natimortos</label><input type="number" min="0" value={form.num_natimortos} onChange={e => setForm(p => ({ ...p, num_natimortos:e.target.value }))}/></div>
                <div className="form-group"><label>Mumificados</label><input type="number" min="0" value={form.num_mumificados} onChange={e => setForm(p => ({ ...p, num_mumificados:e.target.value }))}/></div>
              </div>
              {form.num_crias_vivas > 0 && (
                <div className="form-group">
                  <label>Crias (brinco + sexo + peso ao nascer)</label>
                  {form.crias.map((c, i) => (
                    <div key={i} style={{ display:'flex', gap:8, marginBottom:6, alignItems:'center' }}>
                      <span style={{ fontSize:13, color:'var(--text-secondary)', minWidth:60 }}>Cria {i+1}</span>
                      <input value={c.brinco} onChange={e => { const a=[...form.crias]; a[i]={...a[i],brinco:e.target.value}; setForm(p=>({...p,crias:a})); }} placeholder="Brinco (opcional)"/>
                      <select value={c.sexo} onChange={e => { const a=[...form.crias]; a[i]={...a[i],sexo:e.target.value}; setForm(p=>({...p,crias:a})); }} style={{ width:80 }}>
                        <option value="M">Macho</option><option value="F">Fêmea</option>
                      </select>
                      <input type="number" min="0" step="0.1" value={c.peso_nascimento} onChange={e => { const a=[...form.crias]; a[i]={...a[i],peso_nascimento:e.target.value}; setForm(p=>({...p,crias:a})); }} placeholder="Peso (kg)" style={{ width:110 }}/>
                    </div>
                  ))}
                </div>
              )}
              <div className="form-group"><label>Ocorrências</label><textarea rows={2} value={form.ocorrencias} placeholder="Ex: Retenção de placenta..." onChange={e => setForm(p => ({ ...p, ocorrencias:e.target.value }))}/></div>
              <div className="form-group"><label>Observações</label><textarea rows={2} value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes:e.target.value }))}/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.mae_id}>Registrar Parto</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Novo Parto Previsto (manual, sem cobertura registrada) */}
      {showPrevisto && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowPrevisto(false)}>
          <div className="modal">
            <div className="modal-header"><h2>Novo Parto Previsto</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowPrevisto(false)}>✕</button></div>
            <div className="modal-body">
              <p style={{ fontSize:13, color:'var(--text-secondary)', marginTop:0 }}>
                Para matriz com sinais de prenhez, mesmo sem cobertura registrada no sistema.
              </p>
              <div className="form-group"><label>Matriz *</label>
                <AnimalSearchSelect value={formPrevisto.mae_id} onChange={id => setFormPrevisto(p => ({ ...p, mae_id:id }))} sexo="F" required />
              </div>
              <div className="form-group"><label>Pai (opcional)</label>
                <AnimalSearchSelect value={formPrevisto.pai_id || ''} onChange={id => setFormPrevisto(p => ({ ...p, pai_id:id }))} sexo="M" emptyLabel="Não informado" />
              </div>
              <div className="form-group"><label>Data Prevista do Parto *</label>
                <input type="date" value={formPrevisto.data_prevista} onChange={e => setFormPrevisto(p => ({ ...p, data_prevista:e.target.value }))}/>
              </div>
              <div className="form-group"><label>Observações</label>
                <textarea rows={2} value={formPrevisto.observacoes} onChange={e => setFormPrevisto(p => ({ ...p, observacoes:e.target.value }))}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPrevisto(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarPrevisto} disabled={!formPrevisto.mae_id || !formPrevisto.data_prevista}>Cadastrar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Confirmar Parto Previsto */}
      {confirmando && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setConfirmando(null)}>
          <div className="modal" style={{ maxWidth:640 }}>
            <div className="modal-header">
              <div>
                <h2>Confirmar Parto</h2>
                <p style={{ margin:0, fontSize:13, color:'var(--text-secondary)' }}>
                  Matriz: <strong>{confirmando.mae_brinco}{confirmando.mae_nome ? ` — ${confirmando.mae_nome}` : ''}</strong>
                </p>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmando(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ padding:'10px 14px', background:'#F0FDF4', borderRadius:8, marginBottom:14, fontSize:13 }}>
                As crias serão cadastradas automaticamente com a categoria correta baseada no sexo informado.
                A matriz será atualizada para <strong>Lactante</strong>.
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data Real do Parto *</label><input type="date" value={formConf.data} onChange={e => setFormConf(p => ({ ...p, data:e.target.value }))}/></div>
                <div className="form-group"><label>Hora</label><input type="time" value={formConf.hora} onChange={e => setFormConf(p => ({ ...p, hora:e.target.value }))}/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Tipo do Parto</label>
                  <select value={formConf.tipo} onChange={e => setFormConf(p => ({ ...p, tipo:e.target.value }))}>
                    <option value="normal">Normal</option><option value="distocico">Distócico</option><option value="cesariana">Cesariana</option>
                  </select>
                </div>
                <div className="form-group"><label>Crias Vivas *</label>
                  <input type="number" min="0" value={formConf.num_crias_vivas} onChange={e => updateCriasConf(parseInt(e.target.value)||0)}/>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Natimortos</label><input type="number" min="0" value={formConf.num_natimortos} onChange={e => setFormConf(p => ({ ...p, num_natimortos:e.target.value }))}/></div>
                <div className="form-group"><label>Mumificados</label><input type="number" min="0" value={formConf.num_mumificados} onChange={e => setFormConf(p => ({ ...p, num_mumificados:e.target.value }))}/></div>
              </div>

              {formConf.num_crias_vivas > 0 && (
                <div className="form-group">
                  <label>Crias ({formConf.num_crias_vivas} {formConf.num_crias_vivas === 1 ? 'cria' : 'crias'})</label>
                  <div style={{ padding:'10px 12px', background:'#F8FAFC', borderRadius:6, border:'1px solid #E2E8F0' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'60px 1fr 90px 100px', gap:8, marginBottom:6, fontSize:12, color:'var(--text-secondary)', fontWeight:600, textTransform:'uppercase' }}>
                      <span>#</span><span>Brinco</span><span>Sexo</span><span>Peso (kg)</span>
                    </div>
                    {formConf.crias.map((c, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'60px 1fr 90px 100px', gap:8, marginBottom:6, alignItems:'center' }}>
                        <span style={{ fontSize:13, color:'var(--text-secondary)' }}>Cria {i+1}</span>
                        <input value={c.brinco} onChange={e => setCriaConf(i, 'brinco', e.target.value)} placeholder="Brinco (ex: C001)"/>
                        <select value={c.sexo} onChange={e => setCriaConf(i, 'sexo', e.target.value)}
                          style={{ borderLeft: `3px solid ${SEXO_COR[c.sexo]||'#ccc'}` }}>
                          <option value="M">Macho</option>
                          <option value="F">Fêmea</option>
                        </select>
                        <input type="number" min="0" step="0.1" value={c.peso_nascimento||''} onChange={e => setCriaConf(i, 'peso_nascimento', e.target.value)} placeholder="kg"/>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize:12, color:'var(--text-secondary)', margin:'6px 0 0' }}>
                    Crias sem brinco serão cadastradas com brinco provisório automático.
                  </p>
                </div>
              )}

              <div className="form-group"><label>Ocorrências</label><textarea rows={2} value={formConf.ocorrencias} placeholder="Ex: Retenção de placenta, auxílio ao parto..." onChange={e => setFormConf(p => ({ ...p, ocorrencias:e.target.value }))}/></div>
              <div className="form-group"><label>Observações</label><textarea rows={2} value={formConf.observacoes} onChange={e => setFormConf(p => ({ ...p, observacoes:e.target.value }))}/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmando(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarParto} style={{ background:'#16A34A' }}>
                <CheckCircle size={15}/> Confirmar Parto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
